import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { loadTenant, loadTenantPendingUsers, loadTenantUsersList } from 'src/redux/tenants/actions';
import { selectUser } from 'src/redux/user/selectors';
import { selectUsersError } from 'src/redux/users/selectors';
import {
  selectDeletePendingTenantUsersSuccess,
  selectInviteSharedUserSuccess,
  selectInviteTenantUserSuccess,
  selectResendInviteTenantUsersSuccess,
  selectStorm360Users,
  selectTenant,
  selectTenantPendingUsers,
} from 'src/redux/tenants/selectors';
import { Button, SomethingWentWrongState, Toolbar } from 'src/components';
import { PermissionEnum } from 'src/shared/enums/permission.enum';
import config from 'src/config';

import { Box, BoxProps, Tab, Tabs } from '@mui/material';

import { useTranslation } from 'react-i18next';
import { themeColors } from 'src/theme';
import { DashboardWrapper } from '../wrappers';
import InviteUserDialog from './components/InviteUserDialog';
import InvitationSentDialog from './components/InvitationUserSentDialog';
import PendingUsersTab from './UsersTabs/PendingUsersTab';
import RegisteredUsersTab from './UsersTabs/RegisteredUsersTab';

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export type InvitationInfoType = {
  email: string;
  role: string;
  isResendEmail?: boolean;
};

function TabPanel(props: TabPanelProps & BoxProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflowY: 'hidden',
        ...(value !== index && { display: 'none' }),
      }}
      {...other}
    >
      {value === index && <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'hidden' }}>{children}</Box>}
    </Box>
  );
}

export default function MyUsers() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const users = useSelector(selectStorm360Users);
  const pendingUsers = useSelector(selectTenantPendingUsers);
  const currUser = useSelector(selectUser);
  const company = useSelector(selectTenant);
  const isUsersLoadingError = useSelector(selectUsersError);
  const isInvitingUserSuccess = useSelector(selectInviteTenantUserSuccess);
  const isInvitingSharedUserSuccess = useSelector(selectInviteSharedUserSuccess);
  const isResendInviteSuccess = useSelector(selectResendInviteTenantUsersSuccess);
  const isDeletingUserSuccess = useSelector(selectDeletePendingTenantUsersSuccess);
  const [tabsValue, setTabsValue] = useState<number>(0);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfoType>({
    email: '',
    role: '',
    isResendEmail: false,
  });

  const usersTotal = users.length;

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setInvitationInfo({ email: '', role: '' });
  };

  useEffect(() => {
    if (isInvitingUserSuccess || isInvitingSharedUserSuccess || isResendInviteSuccess) {
      setShowSuccessDialog(true);
    }
  }, [isInvitingUserSuccess, isResendInviteSuccess, isInvitingSharedUserSuccess]);

  useEffect(() => {
    currUser.tenantId && dispatch(loadTenant.init({ id: currUser.tenantId }));
  }, [currUser.tenantId]);

  useEffect(() => {
    if (currUser.tenantId) {
      dispatch(loadTenantUsersList.init({ tenantId: currUser.tenantId }));
    }
  }, [currUser.tenantId]);

  useEffect(() => {
    currUser.tenantId && dispatch(loadTenantPendingUsers.init({ id: currUser.tenantId }));
  }, [currUser.tenantId]);

  useEffect(() => {
    currUser.tenantId && dispatch(loadTenantPendingUsers.init({ id: currUser.tenantId }));
  }, [isInvitingUserSuccess, isDeletingUserSuccess, isInvitingSharedUserSuccess]);

  const handleTabsChange = (event: React.SyntheticEvent, newTab: number) => {
    setTabsValue(newTab);
  };

  if (isUsersLoadingError) {
    return (
      <DashboardWrapper breadcrumbs={[[t('usersPage.userManagement')]]}>
        <SomethingWentWrongState />
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper
      breadcrumbs={[[t('usersPage.userManagement'), usersTotal ? ` (${usersTotal})` : '']]}
      navigationChildren={
        <>
          {(tabsValue === 0 && users?.length) || (tabsValue === 1 && pendingUsers?.length) ? <Toolbar options="mini" /> : null}
          {config.can(PermissionEnum.CREATE_USER) ? (
            <Button onClick={() => setShowInviteDialog(true)} sx={{ p: 0, minWidth: '48px', fontSize: '24px' }}>
              +
            </Button>
          ) : null}
        </>
      }
    >
      <Tabs value={tabsValue} onChange={handleTabsChange} aria-label="basic tabs example">
        <Tab
          label={`${t('usersPage.registered')}`}
          {...a11yProps(0)}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            color: themeColors.grayDark,
            '&.Mui-selected': { color: 'black' },
          }}
        />
        <Tab
          label={`${t('usersPage.pending')}`}
          {...a11yProps(1)}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            color: themeColors.grayDark,
            '&.Mui-selected': { color: 'black' },
          }}
        />
      </Tabs>

      <TabPanel value={tabsValue} index={0}>
        <RegisteredUsersTab users={users} />
      </TabPanel>

      <TabPanel value={tabsValue} index={1}>
        <PendingUsersTab users={pendingUsers} setInviteInfo={(info) => setInvitationInfo(info)} />
      </TabPanel>

      {showInviteDialog && (
        <InviteUserDialog theme="user" setInviteInfo={(info) => setInvitationInfo(info)} onClose={() => setShowInviteDialog(false)} />
      )}
      <InvitationSentDialog
        theme="user"
        open={showSuccessDialog}
        companyName={company.title}
        invitationInfo={invitationInfo}
        onClose={handleCloseSuccessDialog}
      />
    </DashboardWrapper>
  );
}
